/* eslint-disable */
import { defineStore } from 'pinia'

export interface NotificationData {
  id: string
  title: string
  body: string
  icon?: string
  badge?: string
  tag?: string
  data?: any
  timestamp: number
  read: boolean
}

export interface NotificationState {
  permission: NotificationPermission
  subscription: PushSubscription | null
  notifications: NotificationData[]
  isSupported: boolean
  vapidPublicKey: string | null
}

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    permission: 'default',
    subscription: null,
    notifications: [],
    isSupported: false,
    vapidPublicKey: null
  }),

  getters: {
    unreadCount: (state) => state.notifications.filter((n) => !n.read).length,
    canSendNotifications: (state) => state.permission === 'granted' && state.isSupported,
    isSubscribed: (state) => state.subscription !== null
  },

  actions: {
    initializeNotifications() {
      if (typeof window === 'undefined') return

      this.isSupported =
        'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window

      if (this.isSupported) {
        this.permission = Notification.permission
      }
    },

    async requestPermission(): Promise<NotificationPermission> {
      if (!this.isSupported) {
        throw new Error('Notifications are not supported')
      }

      const permission = await Notification.requestPermission()
      this.permission = permission
      return permission
    },

    async subscribe(vapidPublicKey: string): Promise<PushSubscription | null> {
      if (!this.canSendNotifications) {
        throw new Error('Notifications permission not granted')
      }

      try {
        // Check if service worker is already registered and active
        let registration: ServiceWorkerRegistration

        if (navigator.serviceWorker.controller) {
          // Service worker is already controlling the page
          const registrations = await navigator.serviceWorker.getRegistrations()
          registration = registrations[0]

          if (!registration) {
            registration = await navigator.serviceWorker.ready
          }
        } else {
          // Service worker not yet active, wait for ready with timeout
          registration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Service worker ready timeout')), 10000)
            )
          ])
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidPublicKey
        })

        this.subscription = subscription
        this.vapidPublicKey = vapidPublicKey
        return subscription
      } catch (error) {
        console.error('Failed to subscribe to push notifications:', error)
        return null
      }
    },

    async unsubscribe(): Promise<boolean> {
      if (!this.subscription) {
        return true
      }

      try {
        const success = await this.subscription.unsubscribe()
        if (success) {
          this.subscription = null
        }
        return success
      } catch (error) {
        console.error('Failed to unsubscribe from push notifications:', error)
        return false
      }
    },

    async getExistingSubscription(): Promise<PushSubscription | null> {
      if (!this.isSupported) return null

      try {
        // First check if we have a local subscription
        let registration: ServiceWorkerRegistration

        if (navigator.serviceWorker.controller) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          registration = registrations[0]

          if (!registration) {
            registration = await navigator.serviceWorker.ready
          }
        } else {
          registration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Service worker ready timeout')), 10000)
            )
          ])
        }

        const localSubscription = await registration.pushManager.getSubscription()

        // Always check server subscriptions regardless of local subscription
        const serverSubscriptions = await this.getServerSubscriptions()

        // If we have a local subscription, verify it's still active on server
        if (localSubscription) {
          const isActiveOnServer = serverSubscriptions.some(
            (serverSub: any) => serverSub.endpoint === localSubscription.endpoint
          )

          if (isActiveOnServer) {
            this.subscription = localSubscription
            return localSubscription
          } else {
            // Local subscription exists but not on server, clean it up
            await localSubscription.unsubscribe()
            this.subscription = null
          }
        }

        // If no local subscription but server has subscriptions,
        // we need to indicate that subscriptions exist
        if (serverSubscriptions.length > 0) {
          // Create a mock subscription object to indicate server subscription exists
          // This will make isSubscribed return true
          this.subscription = {
            endpoint: serverSubscriptions[0].endpoint,
            expirationTime: null,
            // Mock methods for the PushSubscription interface
            getKey: (keyName: PushEncryptionKeyName) => {
              if (keyName === 'p256dh') {
                return Uint8Array.from(atob(serverSubscriptions[0].public_key), (c) =>
                  c.charCodeAt(0)
                )
              }
              if (keyName === 'auth') {
                return Uint8Array.from(atob(serverSubscriptions[0].auth_token), (c) =>
                  c.charCodeAt(0)
                )
              }
              return null
            },
            options: {
              userVisibleOnly: true,
              applicationServerKey: null
            },
            unsubscribe: async () => {
              // This will be handled by the store's unsubscribe method
              return true
            },
            toJSON: () => ({
              endpoint: serverSubscriptions[0].endpoint,
              keys: {
                p256dh: serverSubscriptions[0].public_key,
                auth: serverSubscriptions[0].auth_token
              }
            })
          } as PushSubscription

          return this.subscription
        }

        this.subscription = null
        return null
      } catch (error) {
        console.error('Failed to get existing subscription:', error)
        return null
      }
    },

    async getServerSubscriptions(): Promise<any[]> {
      try {
        const user = useUserStore()
        const response = await useApi(
          `${user.isLoggedInAsContact ? 'css/web-push' : 'web-push'}/subscriptions`,
          'GET'
        )

        if (response && Array.isArray(response.data)) {
          return response.data
        }

        return []
      } catch (error) {
        console.error('Failed to get server subscriptions:', error)
        return []
      }
    },

    async checkSubscriptionOnServer(endpoint: string): Promise<boolean> {
      try {
        const user = useUserStore()
        const response = await useApi(
          `${user.isLoggedInAsContact ? 'css/web-push' : 'web-push'}/subscriptions`,
          'GET'
        )

        if (response && Array.isArray(response.data)) {
          return response.data.some((subscription: any) => subscription.endpoint === endpoint)
        }

        return false
      } catch (error) {
        console.error('Failed to check subscription on server:', error)
        return false
      }
    },

    addNotification(notification: Omit<NotificationData, 'id' | 'timestamp' | 'read'>) {
      const newNotification: NotificationData = {
        ...notification,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        read: false
      }

      this.notifications.unshift(newNotification)

      // Keep only the last 50 notifications
      if (this.notifications.length > 50) {
        this.notifications = this.notifications.slice(0, 50)
      }
    },

    markAsRead(notificationId: string) {
      const notification = this.notifications.find((n) => n.id === notificationId)
      if (notification) {
        notification.read = true
      }
    },

    markAllAsRead() {
      this.notifications.forEach((n) => (n.read = true))
    },

    removeNotification(notificationId: string) {
      const index = this.notifications.findIndex((n) => n.id === notificationId)
      if (index > -1) {
        this.notifications.splice(index, 1)
      }
    },

    clearAllNotifications() {
      this.notifications = []
    },

    // Helper method to convert VAPID key
    urlBase64ToUint8Array(base64String: string): Uint8Array {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

      const rawData = window.atob(base64)
      const outputArray = new Uint8Array(rawData.length)

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }

      return outputArray
    }
  }
})
